/**
 * Whether the current supplier user may see shared price books — CR-6346.
 *
 * CR-5552 hid contract pricing from suppliers entirely. CR-6346 lets a customer
 * opt in to sharing, so the supplier-side Pricebooks tab is no longer a flat
 * `!isSupplier` decision — it depends on that customer's configuration.
 *
 * Resolved server-side (the sharing flag plus the per-supplier grant) so the
 * client never has to reason about tenant settings it cannot see. Customers and
 * Helix users skip the request entirely: their access is decided elsewhere.
 *
 * @param enabled - Only fetch when true (i.e. the viewer is a supplier).
 * @returns `hasAccess` (false until known) and `loading`.
 */
import { useState, useEffect } from 'react';
import { platformSettingsAPI } from '../lib/api';

export function usePriceBookAccess(enabled: boolean): {
  hasAccess: boolean;
  loading: boolean;
} {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await platformSettingsAPI.getMyPriceBookAccess();
        if (!cancelled) setHasAccess(Boolean(res.data?.has_access));
      } catch (err: unknown) {
        // Fail closed — an unreadable answer must not open the tab.
        console.error('Error resolving price book access:', err);
        if (!cancelled) setHasAccess(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { hasAccess, loading };
}
